import React from 'react';
import styled from 'styled-components';
import Button from './Button'; // Assuming Button component handles loading/disabled states

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isConfirming?: boolean; // To show loading state on confirm button
  confirmButtonVariant?: 'primary' | 'danger' | 'warning'; // For styling
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000; // Ensure it's on top
  opacity: 1;
  transition: opacity 0.2s ease-in-out;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 450px; // Adjust max-width as needed
  display: flex;
  flex-direction: column;
  gap: 16px; // Spacing between elements
`;

const ModalTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 8px 0; // Adjust margin
  color: #333;
`;

const ModalMessage = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: #555;
  margin: 0 0 16px 0; // Adjust margin
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px; // Spacing above buttons

  @media (max-width: 480px) {
    flex-direction: column-reverse; // Stack buttons on small screens
    gap: 10px;
    align-items: stretch; // Make buttons full width
  }
`;

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isConfirming = false,
  confirmButtonVariant = 'primary',
}) => {
  if (!isOpen) {
    return null;
  }

  // Determine button styles based on variant
  let confirmBgColor = '#000'; // Default primary
  let confirmTextColor = '#fff';
  let confirmBorderColor = '#000';

  if (confirmButtonVariant === 'danger') {
    confirmBgColor = '#e53935'; // Red
    confirmTextColor = '#fff';
    confirmBorderColor = '#e53935';
  } else if (confirmButtonVariant === 'warning') {
    confirmBgColor = '#fdd835'; // Yellowish
    confirmTextColor = '#333';
    confirmBorderColor = '#fdd835';
  }
  // Add more variants if needed

  return (
    <ModalOverlay onClick={onCancel}> {/* Close on overlay click */}
      <ModalContent onClick={(e) => e.stopPropagation()}> {/* Prevent closing when clicking inside */}
        <ModalTitle>{title}</ModalTitle>
        <ModalMessage>{message}</ModalMessage>
        <ModalActions>
          <Button
            text={cancelText}
            onClick={onCancel}
            bgColor="#ccc"
            textColor="#555"
            outline={true}
            width="auto"
            minWidth="100px"
            disabled={isConfirming} // Disable cancel while confirming
          />
          <Button
            text={confirmText}
            onClick={onConfirm}
            loading={isConfirming}
            bgColor={confirmBgColor}
            textColor={confirmTextColor}
            outline={false}
            width="auto"
            minWidth="100px"
          />
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ConfirmationModal;